import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { docFileName } from "@/lib/utils";
import api, { formatApiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ScanBarcode, X, ShoppingCart, Search, Receipt, ArrowUp, ArrowDown, ArrowUpDown, Camera, Layers, Rows2, Rows3 } from "lucide-react";
import { formatDOP, formatMoney, formatNum } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Link } from "react-router-dom";
import { PaginationBar } from "@/components/ProductSearchControls";
import CategoryChips from "@/components/CategoryChips";
import PrintMenu from "@/components/PrintMenu";
import BarcodeScannerModal from "@/components/BarcodeScannerModal";
import useBarcodeBuffer from "@/hooks/useBarcodeBuffer";
import { useConfirm } from "@/components/ConfirmDialog";
import { useAuth } from "@/contexts/AuthContext";
import CustomerCombobox from "@/components/CustomerCombobox";
import ProductCombobox from "@/components/ProductCombobox";
import CustomerCreditPanel from "@/components/CustomerCreditPanel";
import PresentationPicker from "@/components/PresentationPicker";
import CartItem from "@/components/pos/CartItem";
import CartTabsBar from "@/components/pos/CartTabsBar";
import { useSuggestedNcf, EXEMPT_SALE_TYPES } from "@/hooks/useSuggestedNcf";
import OrderNotificationsSettings from "@/components/OrderNotificationsSettings";
import bus from "@/lib/eventBus";

const POS_CART_KEY = "pos.cart.v1";
const POS_ACTIVE_CART_KEY = "pos.active_cart.v1";
const POS_COMPACT_PRODUCTS_KEY = "pos.compact_products.v1";

function SortHeader({ col, sortBy, sortDir, onSort, align = "left", children }) {
  const Icon = sortBy !== col ? ArrowUpDown : (sortDir === "asc" ? ArrowUp : ArrowDown);
  return (
    <th
      onClick={() => onSort(col)}
      className={`overline cursor-pointer select-none hover:bg-accent p-2 ${align === "right" ? "text-right" : "text-left"}`}
      data-testid={`pos-sort-${col}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <Icon className={`w-3 h-3 ${sortBy === col ? "text-primary" : "text-muted-foreground/50"}`} />
      </span>
    </th>
  );
}

function loadPersistedCart() {
  try {
    const raw = localStorage.getItem(POS_CART_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (_) { return []; }
}

export default function POS() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [ncfTypes, setNcfTypes] = useState([]);
  const [search, setSearch] = useState("");
  const confirm = useConfirm();
  // POS usa los mismos checkboxes pero con nombres legacy mapeados al backend
  const [criteria, setCriteria] = useState({ sku: true, barcode: true, name: true, description: false });
  const [sortBy, setSortBy] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [cart, setCart] = useState(loadPersistedCart);
  const [customer, setCustomer] = useState(null);
  const [creditStatus, setCreditStatus] = useState(null);
  const [ncfType, setNcfType] = useState("E32");
  const ncfHint = useSuggestedNcf(customer?.id, ncfTypes, setNcfType);
  const [isExport, setIsExport] = useState(false);
  useEffect(() => { setIsExport(false); }, [customer?.id]);
  // (Item 1) Vacío por defecto — obliga al cajero a elegirlo explícitamente
  const [paymentMethod, setPaymentMethod] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [tax, setTax] = useState({ itbis_rate: 18 });
  const [completedInvoice, setCompletedInvoice] = useState(null);
  // (Item 2) Borrador de cantidad por producto durante edición del input
  const [qtyDrafts, setQtyDrafts] = useState({});
  // (Item 6) Ediciones de precio manual por producto (solo admin/gerente)
  const [priceDrafts, setPriceDrafts] = useState({});
  const { user: authUser, can } = useAuth();
  const canDiscount = (authUser?.role === "admin" || authUser?.role === "gerente");
  // Iter 49 — Multi-carrito compartido
  const skipLoadRef = useRef(null);
  const [activeCartId, setActiveCartId] = useState(() => {
    try { return localStorage.getItem(POS_ACTIVE_CART_KEY) || null; } catch (_) { return null; }
  });
  const [cartMeta, setCartMeta] = useState(null); // {number, status, created_by_name}
  const [cartsRefreshTrigger, setCartsRefreshTrigger] = useState(0);
  const [dispatching, setDispatching] = useState(false);
  const [posSettings, setPosSettings] = useState({ require_dispatch_workflow: false });
  const saveDebounceRef = useRef(null);
  // (Item 11) Pago con Nota de Crédito
  const [ncCode, setNcCode] = useState("");
  const [ncInfo, setNcInfo] = useState(null);      // resultado del lookup
  const [ncLookupError, setNcLookupError] = useState("");
  // Filtros laterales por categoría/sub
  const [categoryId, setCategoryId] = useState(null);
  const [subcategoryId, setSubcategoryId] = useState(null);
  // Paginación server-side
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);

  // Iter 54 — Layout: dar más protagonismo al panel de carrito.
  //  - La tabla de productos se fija a un ancho mínimo necesario (por eso truncamos).
  //  - El carrito toma TODO el espacio restante (1fr) → crece automáticamente
  //    cuando el sidebar de la app se colapsa.
  //  - El vendedor puede semi-colapsar la tabla de productos para dejarla en
  //    SKU + Nombre y así darle aún más ancho al carrito.
  const [compactProducts, setCompactProducts] = useState(() => {
    try { return localStorage.getItem(POS_COMPACT_PRODUCTS_KEY) === "1"; } catch (_) { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(POS_COMPACT_PRODUCTS_KEY, compactProducts ? "1" : "0"); } catch (_) { /* noop */ }
  }, [compactProducts]);
  // Ancho FIJO del panel de productos. El carrito toma el resto con minmax(0,1fr).
  const productsColWidth = compactProducts ? "20rem" : "38rem";

  // Persistir carrito en localStorage
  useEffect(() => {
    try { localStorage.setItem(POS_CART_KEY, JSON.stringify(cart)); } catch (_) { /* noop */ }
  }, [cart]);


  const loadProducts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("only_active", "true");
      if (search) params.set("q", search);
      params.set("in_sku", String(criteria.sku));
      params.set("in_name", String(criteria.name));
      params.set("in_barcode", String(criteria.barcode));
      params.set("in_description", String(criteria.description));
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      if (categoryId) params.set("category_id", categoryId);
      if (subcategoryId) params.set("subcategory_id", subcategoryId);
      const { data } = await api.get(`/products?${params.toString()}`);
      setProducts(data.items);
      setTotal(data.total);
      setTotalPages(data.total_pages);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data));
    api.get("/warehouses").then(({ data }) => setWarehouses(data));
    // Iter 48 — Cargar cliente default si el admin lo configuró
    api.get("/customers/default").then(({ data }) => {
      if (data && data.id) setCustomer(data);
    }).catch(() => {});
    api.get("/settings/tax").then(({ data }) => setTax(data || { itbis_rate: 18 }));
    api.get("/settings/pos").then(({ data }) => {
      const cfg = data || {};
      setPosSettings(cfg);
      api.get("/settings/ncf-sequences?usable=true").then(({ data: seqs }) => {
        const saleTypes = (seqs || []).filter((s) => {
          if (!s.active) return false;
          if (!String(s.type_code || "").startsWith("E")) return false;
          if (["E33", "E34", "E41", "E43", "E47"].includes(s.type_code)) return false;
          return true;
        });
        setNcfTypes(saleTypes);
      });
    }).catch(() => {});
  }, []);

  // Productos: server-side con debounce
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(loadProducts, 250);
    return () => clearTimeout(debounceRef.current);
  }, [search, criteria, page, pageSize, categoryId, subcategoryId]);

  // Reset a página 1 cuando cambian filtros / tamaño
  useEffect(() => { setPage(1); }, [search, criteria, pageSize, categoryId, subcategoryId]);

  const catMap = useMemo(() => Object.fromEntries(categories.map((c) => [c.id, c.name])), [categories]);
  const whMap = useMemo(() => Object.fromEntries(warehouses.map((w) => [w.id, w.name])), [warehouses]);

  // Sort local sobre la página actual (no requiere round-trip al backend)
  const filtered = useMemo(() => {
    const getVal = (p) => {
      switch (sortBy) {
        case "sku": return (p.sku || "").toLowerCase();
        case "barcode": return (p.barcode || "").toLowerCase();
        case "category": return (catMap[p.category_id] || "").toLowerCase();
        case "warehouse": return (whMap[p.warehouse_id] || "").toLowerCase();
        case "stock": return Number(p.stock || 0);
        case "price": return Number(p.price || 0);
        default: return (p.name || "").toLowerCase();
      }
    };
    return [...products].sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [products, sortBy, sortDir, catMap, whMap]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(col); setSortDir("asc"); }
  };

  const addToCart = async (p) => {
    // Iter 54 — Auto-crear carrito si no hay uno activo y el usuario trabaja con carritos
    if (!activeCartId && can("pos.create_cart")) {
      const created = await createNewCart({ customer, skipLoad: true });
      if (!created) return;
      // El carrito recién creado ya limpió setCart([]), continuamos agregando el item
    }
    // Iter 40 — Unidad alterna: si el usuario eligió una sub-unidad del combobox,
    // aplicamos su ratio y precio; usamos un cart-key único por producto+unidad.
    const saleUnit = p._selected_sale_unit || null;
    const saleRatio = Number(p._selected_sale_ratio || 1) || 1;
    const salePrice = p._selected_price != null ? Number(p._selected_price) : Number(p.price || 0);
    // Stock disponible en la unidad vendida
    const stockAvail = saleUnit ? Math.floor(Number(p.stock || 0) * saleRatio * 100) / 100 : Number(p.stock || 0);
    if (stockAvail <= 0) return toast.error("Producto sin existencia");
    const cartKey = saleUnit ? `${p.id}::${saleUnit}` : p.id;

    setCart((c) => {
      const existing = c.find((it) => it._cart_key === cartKey);
      if (existing) {
        if (existing.quantity + 1 > stockAvail) {
          toast.error("No hay más stock disponible");
          return c;
        }
        return c.map((it) =>
          it._cart_key === cartKey ? { ...it, quantity: it.quantity + 1 } : it
        );
      }
      return [
        ...c,
        {
          _cart_key: cartKey,
          product_id: p.id,
          sku: p.sku,
          name: saleUnit ? `${p.name} · ${saleUnit}` : p.name,
          base_name: p.name,
          quantity: 1,
          price: salePrice,
          list_price: salePrice,
          cost: saleRatio > 1 ? (Number(p.cost || 0) / saleRatio) : Number(p.cost || 0),
          base_cost: Number(p.cost || 0),
          base_price: Number(p.price || 0),
          discount: 0,
          itbis_applies: p.itbis_applies,
          stock: stockAvail,
          base_stock: Number(p.stock || 0),
          base_unit: p.unit || "UND",
          sale_unit: saleUnit,
          sale_ratio: saleRatio,
          sale_units: p.sale_units || [],
        },
      ];
    });
  };

  // Helper para agregar desde el PresentationPicker (tabla de productos)
  const addFromPicker = (product, unit) => {
    if (!unit) return addToCart(product);
    addToCart({
      ...product,
      _selected_sale_unit: unit.unit,
      _selected_sale_ratio: Number(unit.ratio),
      _selected_price: Number(unit.price),
    });
  };

  // Iter 43 — cambiar de unidad en una línea existente del carrito
  const changeCartUnit = (cartKey, unitValue) => {
    setCart((c) => c.map((it) => {
      if ((it._cart_key || it.product_id) !== cartKey) return it;
      // unitValue = "__BASE__" o el código de la sub-unidad
      if (unitValue === "__BASE__" || !unitValue) {
        return {
          ...it,
          _cart_key: it.product_id,
          name: it.base_name || it.name,
          quantity: 1,
          price: it.base_price ?? it.list_price,
          list_price: it.base_price ?? it.list_price,
          cost: it.base_cost ?? it.cost,
          stock: it.base_stock ?? it.stock,
          sale_unit: null,
          sale_ratio: 1,
        };
      }
      const su = (it.sale_units || []).find((u) => u.unit === unitValue);
      if (!su) return it;
      const ratio = Number(su.ratio) || 1;
      return {
        ...it,
        _cart_key: `${it.product_id}::${unitValue}`,
        name: `${it.base_name || it.name.split(" · ")[0]} · ${unitValue}`,
        quantity: 1,
        price: Number(su.price) || 0,
        list_price: Number(su.price) || 0,
        cost: (it.base_cost ?? 0) / ratio,
        stock: Math.floor((it.base_stock ?? 0) * ratio * 100) / 100,
        sale_unit: unitValue,
        sale_ratio: ratio,
      };
    }));
  };

  // Procesa un código que llegó por escáner USB o cámara
  const processScannedCode = async (code) => {
    const c = (code || "").trim();
    if (!c) return;
    try {
      const { data: prod } = await api.get(
        `/products/by-barcode/${encodeURIComponent(c)}?only_active=true`,
      );
      if (prod) {
        addToCart(prod);
        try { navigator.vibrate?.(40); } catch (_) { /* noop */ }
        toast.success(`+ ${prod.name}`, { duration: 1200 });
      }
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error(`Código no encontrado: ${c}`);
      } else {
        toast.error("Error al buscar producto");
      }
    }
  };

  // Estado para el modal de escáner por cámara
  const [scannerOpen, setScannerOpen] = useState(false);

  // Hook de buffer USB (lectores de código de barras como teclado)
  useBarcodeBuffer({
    enabled: true,
    onScan: (code) => processScannedCode(code),
  });

  const updateQty = (id, qty) => {
    setCart((c) =>
      c.map((it) => {
        const key = it._cart_key || it.product_id;
        if (key !== id) return it;
        if (qty < 0) qty = 0;
        if (qty > it.stock) {
          toast.error("Existencia insuficiente");
          return it;
        }
        return { ...it, quantity: qty };
      }).filter((it) => it.quantity > 0)
    );
    setQtyDrafts((d) => { const nd = { ...d }; delete nd[id]; return nd; });
  };

  // (Item 2) Handlers de edición: no elimina el item hasta que el usuario
  // sale del input o presiona Enter. Mientras esté enfocado, se guarda un
  // borrador en `qtyDrafts` sin tocar el carrito.
  const onQtyDraftChange = (id, raw) => {
    setQtyDrafts((d) => ({ ...d, [id]: raw }));
  };
  const commitQty = (id) => {
    const raw = qtyDrafts[id];
    if (raw === undefined) return; // nunca se editó
    const parsed = Number(raw);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      // Al confirmar con 0/negativo/vacío → elimina el item
      setCart((c) => c.filter((it) => (it._cart_key || it.product_id) !== id));
      setQtyDrafts((d) => { const nd = { ...d }; delete nd[id]; return nd; });
      return;
    }
    updateQty(id, parsed);
  };

  // (Item 6) Precio manual con permiso — sólo se aplica al carrito al confirmar
  const onPriceDraftChange = (id, raw) => {
    setPriceDrafts((d) => ({ ...d, [id]: raw }));
  };
  const commitPrice = (id) => {
    const raw = priceDrafts[id];
    if (raw === undefined) return;
    const parsed = Number(raw);
    const item = cart.find((it) => (it._cart_key || it.product_id) === id);
    if (!item) return;
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setPriceDrafts((d) => { const nd = { ...d }; delete nd[id]; return nd; });
      return;
    }
    // Validar margen mínimo 20% sobre costo (si conocemos el costo)
    const cost = Number(item.cost || 0);
    if (cost > 0 && parsed < cost * 1.20 - 0.005) {
      toast.error(`Precio mínimo permitido: ${(cost * 1.20).toFixed(2)} (margen 20% sobre costo).`);
      return;
    }
    if (parsed > item.list_price) {
      toast.warning("Precio superior al de lista — se aceptará pero no aporta descuento.");
    }
    setCart((c) => c.map((it) => (it._cart_key || it.product_id) === id ? { ...it, price: parsed } : it));
    setPriceDrafts((d) => { const nd = { ...d }; delete nd[id]; return nd; });
  };

  const removeItem = (id) => setCart((c) => c.filter((it) => (it._cart_key || it.product_id) !== id));

  const totals = useMemo(() => {
    let subtotal = 0, itbis = 0, discount = 0;
    const rate = EXEMPT_SALE_TYPES.includes(ncfType) ? 0 : tax.itbis_rate;
    cart.forEach((it) => {
      const line = it.quantity * it.price - it.discount;
      discount += it.discount;
      subtotal += line;
      if (it.itbis_applies) itbis += line * (rate / 100);
    });
    return { subtotal, itbis, discount, total: subtotal + itbis };
  }, [cart, tax, ncfType]);

  // ================== Iter 49 — Multi-Cart handlers ==================
  const loadCart = useCallback(async (cartId) => {
    if (!cartId) return;
    try {
      const { data } = await api.get(`/pos/carts/${cartId}`);
      setCartMeta({ id: data.id, number: data.number, status: data.status, created_by_name: data.created_by_name });
      setCart((data.items || []).map((it) => ({
        _cart_key: it._cart_key || (it.sale_unit ? `${it.product_id}::${it.sale_unit}` : it.product_id),
        product_id: it.product_id,
        sku: it.sku || "",
        name: it.name,
        base_name: it.base_name || it.name,
        quantity: Number(it.quantity),
        price: Number(it.price),
        list_price: Number(it.price),
        cost: 0,
        discount: Number(it.discount || 0),
        itbis_applies: it.itbis_applies !== false,
        stock: 999999,
        base_stock: 999999,
        base_unit: "UND",
        sale_unit: it.sale_unit || null,
        sale_ratio: Number(it.sale_ratio || 1),
        sale_units: [],
      })));
      if (data.customer_id) {
        try {
          const { data: c } = await api.get(`/customers/${data.customer_id}`);
          setCustomer(c);
        } catch (_) { setCustomer({ id: data.customer_id, name: data.customer_name }); }
      } else {
        setCustomer(null);
      }
    } catch (err) {
      toast.error(formatApiError(err));
      setActiveCartId(null);
      try { localStorage.removeItem(POS_ACTIVE_CART_KEY); } catch (_) {}
    }
  }, []);

  useEffect(() => {
    if (activeCartId) {
      // Carrito recién creado al agregar el primer producto: no recargar (borraría el item y el cliente)
      if (skipLoadRef.current === activeCartId) skipLoadRef.current = null;
      else loadCart(activeCartId);
      try { localStorage.setItem(POS_ACTIVE_CART_KEY, activeCartId); } catch (_) {}
    } else {
      setCartMeta(null);
      try { localStorage.removeItem(POS_ACTIVE_CART_KEY); } catch (_) {}
    }
  }, [activeCartId, loadCart]);

  const buildCartPayload = () => ({
    customer_id: customer?.id || null,
    customer_name: customer?.name || "Cliente Genérico",
    customer_rnc_cedula: customer?.rnc_cedula || "",
    items: cart.map((it) => ({
      product_id: it.product_id,
      sku: it.sku || "",
      name: it.name,
      base_name: it.base_name || it.name,
      quantity: Number(it.quantity),
      price: Number(it.price),
      discount: Number(it.discount || 0),
      itbis_applies: it.itbis_applies !== false,
      sale_unit: it.sale_unit || null,
      sale_ratio: Number(it.sale_ratio || 1),
      _cart_key: it._cart_key || it.product_id,
    })),
    notes: "",
  });

  const createNewCart = async (extras = {}) => {
    try {
      // Iter 54 — usar cliente default si está configurado
      let defaultCustomer = null;
      try {
        const { data: dc } = await api.get("/customers/default");
        if (dc && dc.id) defaultCustomer = dc;
      } catch (_) { /* sin default */ }

      const chosen = extras.customer || defaultCustomer;
      const payload = {
        customer_id: chosen?.id || null,
        customer_name: chosen?.name || "Cliente Genérico",
        customer_rnc_cedula: chosen?.rnc_cedula || "",
        items: extras.items || [],
        notes: "",
      };
      const { data } = await api.post("/pos/carts", payload);
      toast.success(`Carrito ${data.number} creado`);
      setCart(extras.setCart || []);
      setCustomer(chosen || null);
      if (extras.skipLoad) { skipLoadRef.current = data.id; setCartMeta(data); }
      setActiveCartId(data.id);
      setCartsRefreshTrigger((x) => x + 1);
      return data;
    } catch (err) {
      toast.error(formatApiError(err));
      return null;
    }
  };

  const saveActiveCart = useCallback(async () => {
    if (!activeCartId) return;
    try {
      await api.put(`/pos/carts/${activeCartId}`, buildCartPayload());
      setCartsRefreshTrigger((x) => x + 1);
    } catch (_err) { /* silent auto-save */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCartId, cart, customer]);

  // Auto-guardado con debounce cuando hay carrito activo
  useEffect(() => {
    if (!activeCartId) return;
    if (saveDebounceRef.current) clearTimeout(saveDebounceRef.current);
    saveDebounceRef.current = setTimeout(() => saveActiveCart(), 900);
    return () => clearTimeout(saveDebounceRef.current);
  }, [cart, customer, activeCartId, saveActiveCart]);

  const dispatchToCashier = async () => {
    if (!activeCartId) return toast.error("No hay carrito activo. Crea uno o selecciona uno del panel.");
    if (cart.length === 0) return toast.error("El carrito no tiene productos");
    if (!customer || !customer.id) return toast.error("Selecciona un cliente antes de despachar");
    setDispatching(true);
    try {
      // Guardar cambios pendientes primero
      await api.put(`/pos/carts/${activeCartId}`, buildCartPayload());
      const { data } = await api.post(`/pos/carts/${activeCartId}/dispatch`, {
        payment_method: paymentMethod || "efectivo",
        ncf_type: ncfType,
        is_export: isExport && ncfType === "E46",
      });
      toast.success(`Orden ${data.number} enviada al cajero`, { duration: 3000 });
      setCart([]);
      setCustomer(null);
      setActiveCartId(null);
      setCartsRefreshTrigger((x) => x + 1);
      bus.emit("orders:refresh");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setDispatching(false);
    }
  };
  // ==============================================================

  const checkout = async () => {
    if (cart.length === 0) return toast.error("Carrito vacío");
    // Cliente obligatorio — evita facturar al cliente equivocado por preselección.
    if (!customer || !customer.id) {
      return toast.error("Selecciona un cliente antes de facturar");
    }
    // (Item 1) Método de pago obligatorio
    if (!paymentMethod) return toast.error("Selecciona un método de pago antes de cobrar");

    // (Iter 34) Validación de crédito — el backend también valida, pero avisamos temprano.
    if (paymentMethod === "credito" && creditStatus) {
      if (creditStatus.is_blocked_by_overdue) {
        return toast.error(creditStatus.block_reasons[0] || "El cliente tiene facturas vencidas — crédito bloqueado");
      }
      if (creditStatus.evaluation && !creditStatus.evaluation.ok) {
        return toast.error(creditStatus.evaluation.reason);
      }
    }
    // (Item 11) Si se elige NC: validar código y monto disponible
    if (paymentMethod === "nc") {
      if (!ncInfo || ncLookupError) return toast.error("Verifica el código de la Nota de Crédito");
      if (Number(ncInfo.total || 0) < Number(totals.total)) {
        return toast.error(`El valor de la NC (${ncInfo.total}) no cubre el total (${totals.total.toFixed(2)})`);
      }
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/invoices", {
        customer_id: customer?.id,
        customer_name: customer?.name || "Cliente Genérico",
        customer_rnc_cedula: customer?.rnc_cedula || "",
        ncf_type: ncfType,
        is_export: isExport && ncfType === "E46",
        payment_method: paymentMethod,
        nc_code: paymentMethod === "nc" ? ncCode.toUpperCase() : "",
        items: cart.map((it) => ({
          product_id: it.product_id,
          sku: it.sku,
          name: it.name,
          quantity: Number(it.quantity),
          price: Number(it.price),
          discount: Number(it.discount),
          itbis_applies: it.itbis_applies,
          sale_unit: it.sale_unit || null,
          sale_ratio: Number(it.sale_ratio || 1),
        })),
      });
      toast.success(`Factura ${data.number} creada (NCF: ${data.ncf})`);
      setCompletedInvoice(data);
      setCart([]);
      setNcCode(""); setNcInfo(null); setNcLookupError("");
      // Iter 49: si venía de un carrito activo, marcarlo como facturado/descartado y refrescar
      if (activeCartId) {
        try { await api.delete(`/pos/carts/${activeCartId}`); } catch (_) {}
        setActiveCartId(null);
        setCartsRefreshTrigger((x) => x + 1);
      }
      // refresh stock (recarga la página actual)
      loadProducts();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // (Item 11) Lookup del código de NC — se dispara al blur o Enter en el input
  const lookupNc = async () => {
    const code = (ncCode || "").trim().toUpperCase();
    if (!code) { setNcInfo(null); setNcLookupError(""); return; }
    if (code.length !== 8) {
      setNcInfo(null);
      setNcLookupError("El código debe tener 8 caracteres");
      return;
    }
    try {
      const { data } = await api.get(`/credit-notes/lookup/${code}`);
      if (data.redemption_status !== "disponible") {
        setNcInfo(null);
        setNcLookupError(`NC ${data.number}: estado ${data.redemption_status}`);
        return;
      }
      setNcInfo(data);
      setNcLookupError("");
    } catch (err) {
      setNcInfo(null);
      setNcLookupError(err?.response?.data?.detail || "Código no encontrado");
    }
  };

  return (
    <PageContainer>
      <PageHeader
        testid="pos-header"
        hideTitle
        actions={
          <div className="flex items-center gap-2">
            <OrderNotificationsSettings />
            <span className="text-xs text-muted-foreground hidden sm:block">Venta rápida · genera NCF automáticamente</span>
          </div>
        }
      />

      {/* Iter 53 — Barra horizontal de carritos en el tope (reemplaza el panel lateral) */}
      <div className="mb-2">
        <CartTabsBar
          activeCartId={activeCartId}
          onSelectCart={setActiveCartId}
          onNewCart={createNewCart}
          refreshTrigger={cartsRefreshTrigger}
        />
      </div>

      <div
        className="grid grid-cols-1 lg:grid-cols-[var(--products-col),minmax(0,1fr)] gap-3"
        style={{ ["--products-col"]: productsColWidth }}
      >
        {/* Product grid */}
        <div className="bg-card border border-border rounded-md flex flex-col min-w-0">
          {/* Iter 48 — Filtro de categorías horizontal integrado */}
          <div className="px-3 pt-2.5 pb-2 border-b border-border/60">
            <CategoryChips
              categoryId={categoryId}
              subcategoryId={subcategoryId}
              onChange={({ category_id, subcategory_id }) => {
                setCategoryId(category_id);
                setSubcategoryId(subcategory_id);
              }}
              testidPrefix="pos-cat"
            />
          </div>

          <div className="p-3 border-b border-border flex items-center gap-2">
            <Search className="w-4 h-4 text-muted-foreground ml-2" />
            <Input
              autoFocus
              value={search}
              data-scanner-target="true"
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={async (e) => {
                if (e.key !== "Enter") return;
                e.preventDefault();
                const q = search.trim();
                if (!q) return;
                // 1) Una sola coincidencia → agregar directo
                if (total === 1 && filtered.length === 1) {
                  addToCart(filtered[0]);
                  setSearch("");
                  return;
                }
                // 2) Varios resultados visibles: si SKU activo y hay match EXACTO de SKU → agregar ese
                if (criteria.sku && filtered.length > 0) {
                  const qLower = q.toLowerCase();
                  const exact = filtered.find(
                    (p) => (p.sku || "").toLowerCase() === qLower
                  );
                  if (exact) {
                    addToCart(exact);
                    setSearch("");
                    return;
                  }
                }
                // 3) SKU activo: buscar SKU EXACTO en DB (otra página)
                if (criteria.sku) {
                  try {
                    const { data: prod } = await api.get(
                      `/products/by-sku/${encodeURIComponent(q)}?only_active=true`
                    );
                    if (prod) {
                      addToCart(prod);
                      setSearch("");
                      return;
                    }
                  } catch (_err) { /* 404 → seguimos al fallback */ }
                }
                // 4) Cero o sin match único
                toast.error(total === 0
                  ? "Búsqueda sin coincidencia"
                  : "Búsqueda sin coincidencia única");
              }}
              placeholder="Buscar producto… (Enter agrega si hay una sola coincidencia)"
              className="border-0 focus-visible:ring-0 h-10"
              data-testid="pos-search"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setScannerOpen(true)}
              className="gap-1.5 shrink-0"
              data-testid="pos-open-scanner"
              title="Escanear código de barras con cámara"
            >
              <Camera className="w-4 h-4" />
              <span className="hidden sm:inline">Escanear</span>
            </Button>
          </div>

          <div className="px-3 py-2 border-b border-border flex flex-wrap items-center gap-4 bg-muted/30" data-testid="pos-criteria">
            <span className="overline text-[10px]">Buscar por:</span>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={criteria.sku}
                onCheckedChange={(v) => setCriteria((c) => ({ ...c, sku: !!v }))}
                data-testid="pos-criteria-sku"
              />
              Código (SKU)
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={criteria.barcode}
                onCheckedChange={(v) => setCriteria((c) => ({ ...c, barcode: !!v }))}
                data-testid="pos-criteria-barcode"
              />
              Código de barras
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={criteria.name}
                onCheckedChange={(v) => setCriteria((c) => ({ ...c, name: !!v }))}
                data-testid="pos-criteria-name"
              />
              Nombre
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={criteria.description}
                onCheckedChange={(v) => setCriteria((c) => ({ ...c, description: !!v }))}
                data-testid="pos-criteria-description"
              />
              Descripción
            </label>
            <span className="ml-auto overline text-[10px] data-cell flex items-center gap-2">
              <span>{loading ? "Buscando…" : `${total} resultados`}</span>
              <button
                type="button"
                onClick={() => setCompactProducts((v) => !v)}
                className="inline-flex items-center gap-1 px-2 py-1 rounded border border-border hover:bg-accent transition-colors normal-case tracking-normal text-[11px] font-semibold"
                data-testid="pos-toggle-compact-products"
                title={compactProducts
                  ? "Expandir tabla de productos (mostrar todas las columnas)"
                  : "Compactar tabla de productos (solo SKU y Nombre) para dar más espacio al carrito"}
              >
                {compactProducts ? <Rows3 className="w-3.5 h-3.5" /> : <Rows2 className="w-3.5 h-3.5" />}
                <span className="hidden md:inline">{compactProducts ? "Expandir" : "Compactar"}</span>
              </button>
            </span>
          </div>

          <div className="overflow-auto max-h-[680px]">
            <table className="w-full text-sm" data-testid="pos-products-table">
              <thead className="sticky top-0 bg-card border-b border-border z-10">
                <tr>
                  <SortHeader col="sku" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>SKU</SortHeader>
                  <SortHeader col="name" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>Nombre</SortHeader>
                  {!compactProducts && (
                    <>
                      <SortHeader col="category" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>Categoría</SortHeader>
                      <SortHeader col="warehouse" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort}>Almacén</SortHeader>
                      <SortHeader col="stock" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right">Existencia</SortHeader>
                      <SortHeader col="price" sortBy={sortBy} sortDir={sortDir} onSort={toggleSort} align="right">Precio</SortHeader>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const out = p.stock <= 0;
                  const hasUnits = Array.isArray(p.sale_units) && p.sale_units.filter((u) => u && u.active !== false).length > 0;
                  const catName = catMap[p.category_id] || "—";
                  const whName = whMap[p.warehouse_id] || "—";
                  const rowContent = (
                    <>
                      <td className="p-2 data-cell text-xs font-bold whitespace-nowrap">{p.sku}</td>
                      <td className="p-2 max-w-0">
                        <div className="font-semibold leading-tight flex items-center gap-1.5 min-w-0">
                          <span
                            className="truncate"
                            title={p.name}
                          >
                            {p.name}
                          </span>
                          {hasUnits && (
                            <span
                              className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded bg-primary/10 text-primary uppercase border border-primary/30 shrink-0"
                              title="Este producto tiene múltiples presentaciones de venta"
                              data-testid={`pos-badge-units-${p.sku}`}
                            >
                              <Layers className="w-3 h-3" />
                              {p.sale_units.filter((u) => u && u.active !== false).length + 1}
                            </span>
                          )}
                        </div>
                        {p.description && !compactProducts && (
                          <div
                            className="text-[11px] text-muted-foreground truncate"
                            title={p.description}
                          >
                            {p.description}
                          </div>
                        )}
                      </td>
                      {!compactProducts && (
                        <>
                          <td className="p-2 text-xs max-w-[110px]">
                            <div className="truncate" title={catName}>{catName}</div>
                          </td>
                          <td className="p-2 text-xs max-w-[110px]">
                            <div className="truncate" title={whName}>{whName}</div>
                          </td>
                          <td className={`p-2 text-right data-cell whitespace-nowrap ${out ? "text-destructive font-bold" : p.stock <= (p.min_stock || 0) ? "text-amber-600 font-semibold" : ""}`}>
                            {formatNum(p.stock)} {p.unit}
                          </td>
                          <td className="p-2 text-right data-cell font-bold text-primary whitespace-nowrap">{formatMoney(p.price)}</td>
                        </>
                      )}
                    </>
                  );
                  const rowClass = `border-b border-border/60 cursor-pointer hover:bg-primary/5 ${out ? "opacity-50 cursor-not-allowed hover:bg-transparent" : ""} ${hasUnits ? "bg-primary/[0.02]" : ""}`;
                  if (hasUnits && !out) {
                    return (
                      <PresentationPicker
                        key={p.id}
                        product={p}
                        onPick={addFromPicker}
                        testid={`pos-picker-${p.sku}`}
                      >
                        <tr
                          className={rowClass}
                          data-testid={`pos-product-${p.sku}`}
                          title="Click para elegir presentación de venta"
                        >
                          {rowContent}
                        </tr>
                      </PresentationPicker>
                    );
                  }
                  return (
                    <tr
                      key={p.id}
                      onClick={() => !out && addToCart(p)}
                      className={rowClass}
                      data-testid={`pos-product-${p.sku}`}
                    >
                      {rowContent}
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={compactProducts ? 2 : 6} className="p-6 text-center text-sm text-muted-foreground">
                    {loading ? "Buscando…" : "Sin resultados"}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <PaginationBar
            page={page}
            pageSize={pageSize}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            testidPrefix="pos-pag"
          />
        </div>

        {/* Cart */}
        <div className="bg-card border border-border rounded-md flex flex-col sticky top-4 h-fit">
          <div className="p-4 border-b border-border flex items-center justify-between gap-2">
            <div>
              <div className="overline">Carrito {cartMeta?.number ? `· ${cartMeta.number}` : ""}</div>
              <h2 className="text-xl font-bold tracking-tight mt-1">Resumen de venta</h2>
              {cartMeta && (
                <p className="text-[11px] text-muted-foreground mt-0.5" data-testid="pos-cart-meta">
                  Creado por {cartMeta.created_by_name || "—"} · Guardado automático
                </p>
              )}
            </div>
            {cart.length > 0 && !activeCartId && (
              <Button
                size="sm"
                variant="ghost"
                onClick={async () => {
                  const ok = await confirm({
                    title: "¿Vaciar el carrito?",
                    description: "Se perderán los productos agregados.",
                    confirmLabel: "Vaciar",
                    destructive: true,
                  });
                  if (ok) setCart([]);
                }}
                data-testid="pos-clear-cart"
                className="text-destructive hover:text-destructive gap-1"
                title="Limpiar carrito"
              >
                <X className="w-4 h-4" />
                <span className="text-xs">Limpiar</span>
              </Button>
            )}
          </div>

          <div className="p-4 space-y-3 border-b border-border">
            <div>
              <Label className="overline" htmlFor="pos-customer-combobox">Cliente <span className="text-destructive">*</span></Label>
              <div className="mt-1">
                <CustomerCombobox
                  value={customer}
                  onChange={setCustomer}
                  placeholder="Buscar y seleccionar cliente…"
                  required
                  testid="pos-customer-select"
                />
              </div>
              {!customer && (
                <div className="text-[11px] text-muted-foreground mt-1" data-testid="pos-customer-hint">
                  Debes seleccionar un cliente antes de facturar.
                </div>
              )}
            </div>
            {/* Panel de estado de crédito — solo cuando método = crédito */}
            {customer && paymentMethod === "credito" && (
              <CustomerCreditPanel
                customerId={customer.id}
                saleAmount={totals.total}
                onStatusChange={setCreditStatus}
                testid="pos-credit-status"
              />
            )}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="overline">
                  Tipo NCF {!can("pos.direct_invoice") && <span className="text-[9px] opacity-60">(sugerencia)</span>}
                </Label>
                <Select value={ncfType} onValueChange={setNcfType}>
                  <SelectTrigger className="mt-1" data-testid="pos-ncf-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ncfTypes.map((s) => (
                      <SelectItem key={s.id} value={s.type_code}>
                        {s.type_code} — {s.description}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {ncfHint && !(isExport && ncfType === "E46") && (
                  <p className={`text-[11px] mt-1 ${ncfHint.type === ncfType ? "text-muted-foreground" : "text-amber-700"}`} data-testid="pos-ncf-hint">
                    Sugerido: <b>{ncfHint.type}</b> · {ncfHint.reason}{ncfHint.type !== ncfType ? " (cambiado por el cajero)" : ""}
                  </p>
                )}
                {EXEMPT_SALE_TYPES.includes(ncfType) && (
                  <p className="text-[11px] mt-1 text-primary" data-testid="pos-ncf-exempt">{ncfType} va sin ITBIS (exento).</p>
                )}
                {(ncfHint?.export_possible || ncfType === "E46") && (
                  <label className="flex items-center gap-1.5 text-[11px] mt-1" data-testid="pos-export-toggle">
                    <input type="checkbox" checked={isExport} onChange={(e) => { setIsExport(e.target.checked); setNcfType(e.target.checked ? "E46" : "E32"); }} data-testid="pos-export-checkbox" />
                    Exportación (bienes o servicios salen del país) → E46
                  </label>
                )}
              </div>
              <div>
                <Label className="overline">
                  Pago {!can("pos.direct_invoice") && <span className="text-[9px] opacity-60">(sugerencia)</span>}
                </Label>
                <Select
                  value={paymentMethod}
                  onValueChange={(v) => {
                    setPaymentMethod(v);
                    if (v !== "nc") { setNcCode(""); setNcInfo(null); setNcLookupError(""); }
                  }}
                >
                  <SelectTrigger
                    className={`mt-1 ${!paymentMethod && can("pos.direct_invoice") ? "border-destructive text-destructive" : ""}`}
                    data-testid="pos-payment-select"
                  >
                    <SelectValue placeholder="Elige…" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="tarjeta">Tarjeta</SelectItem>
                    <SelectItem value="transferencia">Transferencia</SelectItem>
                    <SelectItem value="credito">Crédito</SelectItem>
                    <SelectItem value="nc">Nota de Crédito</SelectItem>
                  </SelectContent>
                </Select>
                {paymentMethod === "nc" && can("pos.direct_invoice") && (
                  <div className="mt-2">
                    <Input
                      type="text"
                      value={ncCode}
                      onChange={(e) => { setNcCode(e.target.value.toUpperCase()); setNcInfo(null); setNcLookupError(""); }}
                      onBlur={lookupNc}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.target.blur(); } }}
                      placeholder="Código NC (8 chars)"
                      maxLength={8}
                      className={`font-mono uppercase tracking-widest text-center ${ncLookupError ? "border-destructive" : ncInfo ? "border-emerald-600" : ""}`}
                      data-testid="pos-nc-code-input"
                    />
                    {ncLookupError && (
                      <p className="text-[11px] text-destructive mt-1" data-testid="pos-nc-error">{ncLookupError}</p>
                    )}
                    {ncInfo && !ncLookupError && (
                      <p className="text-[11px] text-emerald-700 dark:text-emerald-400 mt-1" data-testid="pos-nc-info">
                        NC {ncInfo.number} · Disponible: {formatDOP(ncInfo.total)}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="max-h-[520px] overflow-y-auto" data-testid="pos-cart">
            {/* Auto-complete: agregar productos por SKU/nombre sin tocar la tabla */}
            <div className="p-3 border-b border-border sticky top-0 bg-card z-10">
              <ProductCombobox
                testid="pos-quick-add"
                placeholder="Buscar producto: SKU, código de barras o nombre… (los productos con sub-unidades aparecen con etiqueta 'Suelto')"
                onPick={(p) => addToCart(p)}
              />
              <div className="text-[10px] text-muted-foreground mt-1">
                💡 Tip: Puedes cambiar la unidad de venta después de agregar el producto al carrito
              </div>
            </div>
            {cart.length === 0 && (
              <div className="p-6 text-center text-sm text-muted-foreground">
                <ShoppingCart className="w-6 h-6 mx-auto mb-2 opacity-30" />
                Carrito vacío
              </div>
            )}
            {cart.map((it) => {
              const key = it._cart_key || it.product_id;
              return (
                <CartItem
                  key={key}
                  item={it}
                  qtyDraft={qtyDrafts[key]}
                  priceDraft={priceDrafts[key]}
                  canDiscount={canDiscount}
                  onChangeUnit={changeCartUnit}
                  onQtyDraftChange={onQtyDraftChange}
                  onQtyCommit={commitQty}
                  onPriceDraftChange={onPriceDraftChange}
                  onPriceCommit={commitPrice}
                  onUpdateQty={updateQty}
                  onRemove={removeItem}
                />
              );
            })}
          </div>

          <div className="p-4 border-t border-border space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="data-cell">{formatDOP(totals.subtotal)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">ITBIS ({EXEMPT_SALE_TYPES.includes(ncfType) ? 0 : tax.itbis_rate}%)</span><span className="data-cell">{formatDOP(totals.itbis)}</span></div>
            <div className="flex justify-between text-base font-bold pt-2 border-t border-border mt-2"><span>Total</span><span className="data-cell text-primary">{formatDOP(totals.total)}</span></div>
          </div>

          <div className="p-4 space-y-2">
            {/* Iter 49 — Flujos disponibles según política y config */}
            {activeCartId && can("pos.dispatch_cart") && (
              <Button
                onClick={dispatchToCashier}
                disabled={dispatching || cart.length === 0 || !customer}
                className="w-full h-11 bg-amber-600 hover:bg-amber-700 text-white font-bold"
                data-testid="pos-dispatch-btn"
                title="Enviar orden al cajero para cobrar y facturar"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                {dispatching ? "Enviando…" : "Despachar a cajero"}
              </Button>
            )}
            {activeCartId && (
              <Button
                onClick={saveActiveCart}
                variant="outline"
                className="w-full h-9"
                data-testid="pos-save-cart-btn"
                title="Guardar cambios ahora"
              >
                Guardar en espera
              </Button>
            )}
            {can("pos.direct_invoice") && (
              <Button
                onClick={checkout}
                disabled={
                  submitting || cart.length === 0 || !paymentMethod || !customer ||
                  (paymentMethod === "nc" && !ncInfo) ||
                  (paymentMethod === "credito" && creditStatus && (
                    creditStatus.is_blocked_by_overdue ||
                    (creditStatus.evaluation && !creditStatus.evaluation.ok)
                  ))
                }
                className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-bold text-base"
                data-testid="pos-checkout-btn"
              >
                <Receipt className="w-4 h-4 mr-2" />
                {submitting ? "Procesando…" : "Cobrar y emitir NCF"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Confirmation dialog */}
      <Dialog open={!!completedInvoice} onOpenChange={(v) => !v && setCompletedInvoice(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Venta completada</DialogTitle>
          </DialogHeader>
          {completedInvoice && (
            <div className="space-y-3 text-sm">
              <div className="overline">Factura</div>
              <div className="data-cell text-2xl font-black">{completedInvoice.number}</div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <div className="overline">NCF</div>
                  <div className="data-cell font-bold">{completedInvoice.ncf}</div>
                </div>
                <div>
                  <div className="overline">Total</div>
                  <div className="data-cell font-bold text-primary">{formatDOP(completedInvoice.total)}</div>
                </div>
              </div>
              {completedInvoice.is_electronic && (
                <div className="bg-info/10 border border-info/30 p-3 rounded-md text-xs">
                  Esta factura es e-CF. El XML se generó como borrador. Envía a DGII desde la lista de Facturas.
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompletedInvoice(null)}>Cerrar</Button>
            {completedInvoice && (
              <PrintMenu
                url={`/invoices/${completedInvoice.id}/pdf`}
                filename={docFileName(completedInvoice)}
                label="Imprimir factura"
                testid="pos-completed-print"
              />
            )}
            <Link to={`/sales`}>
              <Button className="bg-primary font-bold">Ver Facturas</Button>
            </Link>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de escáner por cámara */}
      <BarcodeScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetected={(code) => {
          setScannerOpen(false);
          processScannedCode(code);
        }}
      />
    </PageContainer>
  );
}
